<script setup>
import { computed, onMounted, onUnmounted, ref, nextTick } from 'vue';

const props = defineProps({
    align: {
        default: 'right'
    },
    width: {
        default: '48'
    },
    contentClasses: {
        default: () => ['py-1', 'bg-white']
    },
    useActive: {
        default: false
    },
    active: {
        default: false
    }
});

const closeOnEscape = (e) => {
    if (open.value && e.key === 'Escape') {
        open.value = false;
    }
};

onMounted(() => document.addEventListener('keydown', closeOnEscape));
onUnmounted(() => document.removeEventListener('keydown', closeOnEscape));

const widthClass = computed(() => {
    return {
        '48': 'w-48',
        '60': 'w-60',
    }[props.width.toString()];
    
});



const open = ref(false);
const autoAlign = ref(null);
const triggerRef = ref(null);
const contentRef = ref(null);

const toggleDropdown = async () => {
    open.value = !open.value;
    if (open.value) {
        autoAlign.value = null; // Reset to default first
        await nextTick();
        calculatePosition();
    }
};

const calculatePosition = () => {
    if (!triggerRef.value || !contentRef.value) return;

    // Ensure we can get the actual height even if transition hasn't fully rendered it
    const isHidden = contentRef.value.style.display === 'none';
    if (isHidden) {
        contentRef.value.style.display = 'block';
        contentRef.value.style.visibility = 'hidden';
    }

    const triggerRect = triggerRef.value.getBoundingClientRect();
    const contentRect = contentRef.value.getBoundingClientRect();

    if (isHidden) {
        contentRef.value.style.display = 'none';
        contentRef.value.style.visibility = '';
    }

    // Find the closest parent that might clip the dropdown
    let clippingParent = triggerRef.value.parentElement;
    while (clippingParent && clippingParent !== document.body) {
        const style = window.getComputedStyle(clippingParent);
        if (style.overflow === 'hidden' || style.overflow === 'auto' || style.overflow === 'scroll' ||
            style.overflowY === 'hidden' || style.overflowY === 'auto' || style.overflowY === 'scroll' ||
            style.overflowX === 'hidden' || style.overflowX === 'auto' || style.overflowX === 'scroll') {
            break;
        }
        clippingParent = clippingParent.parentElement;
    }

    let spaceBelow, spaceAbove;
    if (clippingParent && clippingParent !== document.body) {
        const parentRect = clippingParent.getBoundingClientRect();
        spaceBelow = parentRect.bottom - triggerRect.bottom;
        spaceAbove = triggerRect.top - parentRect.top;
    } else {
        spaceBelow = window.innerHeight - triggerRect.bottom;
        spaceAbove = triggerRect.top;
    }

    // Use actual height or a minimum fallback in case animation scales it to 0 initially
    const height = Math.max(contentRect.height, 100);

    // Check if it will overflow the bottom of the clipping container
    if (spaceBelow < height && spaceAbove > spaceBelow) {
        const alignStr = props.align.toString();
        if (alignStr === 'right') autoAlign.value = 'top-right';
        else if (alignStr === 'Right') autoAlign.value = 'top-Right';
        else if (alignStr === 'left') autoAlign.value = 'top-left';
        else if (alignStr === 'leftRight') autoAlign.value = 'top-leftRight';
        else autoAlign.value = 'top';
    } else {
        autoAlign.value = props.align.toString();
    }
};

const alignmentClasses = computed(() => {
    const align = autoAlign.value || props.align.toString();

    if (align === 'leftRight') {
        return 'origin-top-right lg:origin-top-left right-0 lg:left-0 top-full mt-2';
    } else if (align === 'right') {
        return 'origin-top-right right-0 top-full mt-2';
    } else if (align === 'Right') {
        // Original behavior for 'Right' (fell to the else block which was origin-top left-aligned)
        return 'origin-top-left left-0 top-full mt-2';
    } else if (align === 'left') {
        return 'origin-top-left left-0 top-full mt-2';
    } else if (align === 'top-left') {
        return 'origin-bottom-left bottom-full left-0 mb-2';
    } else if (align === 'top-right') {
        return 'origin-bottom-right bottom-full right-0 mb-2';
    } else if (align === 'top-Right') {
        // Drop-up equivalent of 'Right'
        return 'origin-bottom-left bottom-full left-0 mb-2';
    } else if (align === 'top-leftRight') {
        return 'origin-bottom-right lg:origin-bottom-left bottom-full right-0 lg:left-0 mb-2';
    } else if (align === 'top') {
        return 'origin-bottom bottom-full mb-2';
    } else {
        return 'origin-top top-full mt-2';
    }
});

// LRR
const classes = computed(() => props.active
    ? 'flex items-center h-full px-1 pt-1 border-b-2 border-rose-600 text-sm font-medium leading-5 text-gray-900 focus:outline-none focus:border-rose-700 transition  duration-150 ease-in-out'
    : 'flex items-center h-full px-1 pt-1 border-b-2 border-transparent text-sm font-medium leading-5 text-gray-500 hover:text-gray-700 hover:border-gray-300 focus:outline-none focus:text-gray-700 focus:border-gray-300 transition duration-150 ease-in-out'
);

</script>

<template>
    <div class="relative">
        <div ref="triggerRef" @click="toggleDropdown" :class="(useActive ? classes : '')">
            <slot name="trigger" />
        </div>

        <!-- Full Screen Dropdown Overlay -->
        <div v-show="open" class="fixed inset-0 z-40" @click="open = false"></div>

        <transition
            enter-active-class="transition ease-out duration-200"
            enter-from-class="transform opacity-0 scale-95"
            enter-to-class="transform opacity-100 scale-100"
            leave-active-class="transition ease-in duration-75"
            leave-from-class="transform opacity-100 scale-100"
            leave-to-class="transform opacity-0 scale-95">
            <div v-show="open"
                    ref="contentRef"
                    class="absolute z-50 rounded-md shadow-lg"
                    :class="[widthClass, alignmentClasses, (useActive ? '-mt-2' : '')]"
                    @click="open = false">
                <div class="rounded-md ring-1 ring-black ring-opacity-5" :class="contentClasses">
                    <slot name="content" />
                </div>
            </div>
        </transition>
    </div>
</template>
